import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Container,
  Flex,
  Heading,
  HStack,
  Stack,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Layout from "../../../components/student/Layout/layout";
import AddVoucher from "../../../containers/student/models/add-voucher";
import ExamCard from "../../../containers/student/studyWithEnough/ExamCard";
import VideoCard from "../../../containers/student/video/video";
import { getChapterExamRequest } from "../../../modules/students-modules/exams/Actions";
import { getVideoByIdRequest } from "../../../modules/students-modules/videos/Actions";

const StudyWithEnoughExams = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const chapterExams = useSelector((state) => state.chapterExams.chapterExam);
  const isLoading = useSelector((state) => state.chapterExams.isLoading);
  const [selectedindex, setSelectedIndex] = useState(null);
  const { id } = useParams();
  const [selectedData, setSelectedData] = useState({});

  useEffect(() => {
    dispatch(getChapterExamRequest(id));
  }, [id]);

  // Group exams by chapter
  const groupExamsByChapter = (exams) => {
    if (!exams || !Array.isArray(exams)) return {};
    
    return exams.reduce((groups, exam) => {
      const chapterId = exam.chapter?.id;
      const chapterName = exam.chapter?.name || 'Unknown Chapter';
      
      if (!groups[chapterId]) {
        groups[chapterId] = {
          chapterName,
          exams: []
        };
      }
      
      groups[chapterId].exams.push(exam);
      return groups;
    }, {});
  };

  // Group videos by chapter
  const groupVideosByChapter = (videos) => {
    if (!videos || !Array.isArray(videos)) return {};
    
    return videos.reduce((groups, video) => {
      const chapterId = video.chapter?.id;
      const chapterName = video.chapter?.name || 'Unknown Chapter';
      
      if (!groups[chapterId]) {
        groups[chapterId] = {
          chapterName,
          videos: []
        };
      }
      
      groups[chapterId].videos.push(video);
      return groups;
    }, {});
  };

  const groupedExams = groupExamsByChapter(chapterExams?.exams);
  const groupedVideos = groupVideosByChapter(chapterExams?.videos);

  return (
    <Layout color="main_1" bg="white" isLoading={isLoading}>
      <Container maxW="container.2xl" h="100%" mt="100px" mb="165px" w="100%">
        <HStack w={"100%"}>
          <Heading as="h3" color="main_1" textAlign="center" w="100%">
            Study with Agwad
          </Heading>
        </HStack>
        <Stack
          my={10}
          w="100%"
          flexDirection={{ base: "column", md: "row" }}
          flexWrap="wrap"
          alignItems="start"
          justifyContent="space-between"
          gap={5}
        >
          {/* Exams Section */}
          <Flex w={{ base: "100%", md: "48%" }}>
            <Accordion defaultIndex={[0]} allowMultiple w="100%">
              <AccordionItem>
                <h2>
                  <AccordionButton
                    bg="main_1"
                    color="white"
                    borderRadius="8px"
                    _focus={{ outline: "none" }}
                    _hover={{ bg: "main_1" }}
                  >
                    <Box as="span" flex="1" textAlign="left">
                      Exams
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel>
                  <Accordion allowMultiple w="100%">
                    {Object.entries(groupedExams).map(([chapterId, chapterData]) => (
                      <AccordionItem key={chapterId} border="none">
                        <h3>
                          <AccordionButton
                            bg="gray.100"
                            color="main_1"
                            borderRadius="6px"
                            mb={2}
                            _focus={{ outline: "none" }}
                            _hover={{ bg: "gray.200" }}
                          >
                            <Box as="span" flex="1" textAlign="left" fontSize="sm" fontWeight="semibold">
                              {chapterData.chapterName}
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                        </h3>
                        <AccordionPanel pb={2}>
                          <VStack spacing={2} align="stretch">
                            {chapterData.exams.map((exam, idx) => (
                              <ExamCard exam={exam} key={`${chapterId}-${idx}`} />
                            ))}
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Flex>

          {/* Videos Section */}
          <Flex w={{ base: "100%", md: "48%" }}>
            <Accordion defaultIndex={[0]} allowMultiple w="100%">
              <AccordionItem>
                <h2>
                  <AccordionButton
                    bg="main_1"
                    color="white"
                    borderRadius="8px"
                    _focus={{ outline: "none" }}
                    _hover={{ bg: "main_1" }}
                  >
                    <Box as="span" flex="1" textAlign="left">
                      Videos
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel>
                  <Accordion allowMultiple w="100%">
                    {Object.entries(groupedVideos).map(([chapterId, chapterData]) => (
                      <AccordionItem key={chapterId} border="none">
                        <h3>
                          <AccordionButton
                            bg="gray.100"
                            color="main_1"
                            borderRadius="6px"
                            mb={2}
                            _focus={{ outline: "none" }}
                            _hover={{ bg: "gray.200" }}
                          >
                            <Box as="span" flex="1" textAlign="left" fontSize="sm" fontWeight="semibold">
                              {chapterData.chapterName}
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                        </h3>
                        <AccordionPanel pb={2}>
                          <VStack spacing={2} align="stretch">
                            {chapterData.videos.map((data, idx) => {
                              const globalIndex = chapterExams?.videos?.findIndex(v => v.id === data.id) ?? idx;
                              return (
                                <Box
                                  my={2}
                                  borderRadius="8px"
                                  w="100%"
                                  cursor="pointer"
                                  boxShadow={
                                    selectedData?.id == data?.id
                                      ? "2px 3px 10px #80808045"
                                      : "none"
                                  }
                                  key={`${chapterId}-${idx}`}
                                  onClick={() => {
                                    setSelectedData(data);

                                    if (
                                      data?.max_allowed_views <=
                                        data?.views_count?.views_count ||
                                      (data?.suspended == 1 &&
                                        data?.user_video_suspended != 0)
                                    ) {
                                    } else {
                                      dispatch(getVideoByIdRequest(data?.id));
                                      navigate(
                                        `/study-with-enough/video-preview/${data?.id}?name=${data?.chapter?.name}&video=${data?.id}`
                                      );
                                      setSelectedIndex(globalIndex);
                                    }
                                  }}
                                >
                                  <VideoCard
                                    data={data}
                                    index={globalIndex}
                                    selectedindex={selectedindex}
                                  />
                                </Box>
                              );
                            })}
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Flex>
        </Stack>
        <AddVoucher video_id={selectedData?.id} />
      </Container>
    </Layout>
  );
};

export default StudyWithEnoughExams;
